import React from 'react';
import type {
  AdminCompanyDto,
  AdminLicenseDetailDto,
  AdminLicenseStatus,
} from '../types';
import { formatAdminUtcDate } from '../lib/adminDates';
import {
  CREATABLE_LICENSE_TYPES,
  DEMO_EXPORT_ELEMENT_QUOTA,
  getLicenseTypeLabel,
  type LicenseDraft,
  type LicenseDraftErrors,
} from '../lib/adminLicenses';

interface LicenseFormProps {
  companies: readonly AdminCompanyDto[];
  draft: LicenseDraft;
  errors: LicenseDraftErrors;
  serverLimitError: string | null;
  setField: <Key extends keyof LicenseDraft>(key: Key, value: LicenseDraft[Key]) => void;
}

const FieldError: React.FC<{ message?: string | null }> = ({ message }) =>
  message ? <span className="admin-license-field__error" role="alert">{message}</span> : null;

const LegacyTypeOption: React.FC<{ type: number }> = ({ type }) => {
  if (CREATABLE_LICENSE_TYPES.some((option) => option.value === type)) {
    return null;
  }
  return <option value={type} disabled>{getLicenseTypeLabel(type)} (legacy)</option>;
};

export const LicenseForm: React.FC<LicenseFormProps> = ({
  companies,
  draft,
  errors,
  serverLimitError,
  setField,
}) => (
  <div className="admin-license-form">
    <div className="admin-license-field">
      <label htmlFor="license-company">Company</label>
      <select id="license-company" value={draft.companyId} onChange={(event) => setField('companyId', Number(event.target.value))}>
        <option value={0}>Choose company</option>
        {companies.map((company) => <option key={company.id} value={company.id}>{company.name}</option>)}
      </select>
      <FieldError message={errors.companyId} />
    </div>
    <div className="admin-license-field">
      <label htmlFor="license-type">Type</label>
      <select
        id="license-type"
        value={draft.type}
        onChange={(event) => {
          const type = Number(event.target.value);
          setField('type', type);
          if (type === 2 && draft.remainingExportElements === null) {
            setField('remainingExportElements', DEMO_EXPORT_ELEMENT_QUOTA);
          }
        }}
      >
        <LegacyTypeOption type={draft.type} />
        {CREATABLE_LICENSE_TYPES.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
      </select>
      <FieldError message={errors.type} />
    </div>
    <div className="admin-license-field">
      <label htmlFor="license-instances">Max instances</label>
      <input id="license-instances" type="number" min={1} value={draft.allowedInstances} aria-invalid={Boolean(errors.allowedInstances || serverLimitError)} onChange={(event) => setField('allowedInstances', Number(event.target.value))} />
      <FieldError message={errors.allowedInstances ?? serverLimitError} />
    </div>
    <div className="admin-license-field">
      <label htmlFor="license-registrations">Registration limit</label>
      <input id="license-registrations" type="number" min={1} value={draft.allowedRegistrations} aria-invalid={Boolean(errors.allowedRegistrations)} onChange={(event) => setField('allowedRegistrations', Number(event.target.value))} />
      <FieldError message={errors.allowedRegistrations} />
    </div>
    <div className="admin-license-field">
      <label htmlFor="license-export-elements">Export elements remaining</label>
      <div className="admin-license-quota-control">
        <input
          id="license-export-elements"
          type="number"
          min={0}
          step={1}
          value={Number.isNaN(draft.remainingExportElements) ? '' : draft.remainingExportElements ?? ''}
          disabled={draft.remainingExportElements === null}
          aria-invalid={Boolean(errors.remainingExportElements)}
          onChange={(event) => setField(
            'remainingExportElements',
            event.target.value === '' ? Number.NaN : Number(event.target.value)
          )}
        />
        <label className="admin-license-quota-unlimited" htmlFor="license-export-elements-unlimited">
          <input
            id="license-export-elements-unlimited"
            type="checkbox"
            checked={draft.remainingExportElements === null}
            onChange={(event) => setField(
              'remainingExportElements',
              event.target.checked ? null : DEMO_EXPORT_ELEMENT_QUOTA
            )}
          />
          Unlimited
        </label>
      </div>
      <FieldError message={errors.remainingExportElements} />
    </div>
    <div className="admin-license-field">
      <label htmlFor="license-expires">Expires</label>
      <input id="license-expires" type="date" value={draft.dueDate} aria-invalid={Boolean(errors.dueDate)} onChange={(event) => setField('dueDate', event.target.value)} />
      <FieldError message={errors.dueDate} />
    </div>
    <div className="admin-license-field">
      <label htmlFor="license-status">Status</label>
      <select id="license-status" value={draft.status} onChange={(event) => setField('status', event.target.value as AdminLicenseStatus)}>
        <option value="active">Active</option>
        <option value="suspended">Suspended</option>
        <option value="expired">Expired</option>
      </select>
    </div>
    <div className="admin-license-field admin-license-field--wide">
      <label htmlFor="license-notes">Notes</label>
      <textarea id="license-notes" maxLength={10000} placeholder={'Internal notes\u2026'} value={draft.notes} onChange={(event) => setField('notes', event.target.value)} />
      <FieldError message={errors.notes} />
    </div>
  </div>
);

interface LicenseMachineListProps {
  license: AdminLicenseDetailDto;
  maximum: number;
  pendingIds: readonly number[];
  toggleRevoke: (machineId: number) => void;
}

export const LicenseMachineList: React.FC<LicenseMachineListProps> = ({
  license,
  maximum,
  pendingIds,
  toggleRevoke,
}) => (
  <section className="admin-license-machines" aria-label="Active machines">
    <div className="admin-license-machines__heading">
      <span>Active machines</span>
      <span>{license.machines.length} / {maximum}</span>
    </div>
    {license.machines.length === 0 ? (
      <div className="admin-license-machines__empty">No active machines.</div>
    ) : license.machines.map((machine) => {
      const pending = pendingIds.includes(machine.id);
      return (
        <div className={`admin-license-machine${pending ? ' admin-license-machine--pending' : ''}`} key={machine.id}>
          <div className="admin-license-machine__identity">
            <span className="admin-license-machine__online" data-online={String(machine.isOnline)} aria-label={machine.isOnline ? 'Online' : 'Offline'} />
            <span className="admin-license-machine__host">{machine.host}</span>
            {pending && <span className="admin-license-machine__pending-label">Pending revoke</span>}
          </div>
          <div className="admin-license-machine__meta">
            <span>{machine.userEmail || '\u2014'}</span>
            <span>{formatAdminUtcDate(machine.activatedAt)}</span>
          </div>
          <button
            aria-label={`${pending ? 'Undo revoke' : 'Revoke'} ${machine.host}`}
            className="admin-license-machine__action"
            type="button"
            onClick={() => toggleRevoke(machine.id)}
          >
            {pending ? 'Undo revoke' : 'Revoke'}
          </button>
        </div>
      );
    })}
  </section>
);
